import * as React from 'react'
import {
  IdKeyedObjectShape,
  CustomerUser,
  ApiStatus,
  CustomerDevice,
  User,
  CustomerAdmin,
} from './base'
import {
  UserViewModel,
  CustomerDeviceViewModel,
  CustomerUserViewModel,
  LicensedAgencyViewModel,
  CustomerAdminViewModel,
  MapLayer,
  AddMapLayerResult,
  EditMapLayerResult,
} from '../api'
import { IdentifiableModel } from 'cs-kevlar-react/lib/es/base'
import { idObjectToArray, asIdentifiable } from 'utils'

export interface CurrentAgencyState {
  agency: ApiStatus<LicensedAgencyViewModel>
  agencyUsers: ApiStatus<User[]>
  agencyUserLookup: IdKeyedObjectShape<User>
  licensedUsers: ApiStatus<CustomerUser[]>
  licensedUserLookup: IdKeyedObjectShape<CustomerUser>
  nonLicensedUsers: User[]
  adminUsers: ApiStatus<CustomerAdmin[]>
  adminUserLookup: IdKeyedObjectShape<CustomerAdmin>
  nonAdminUsers: User[]
  devices: ApiStatus<CustomerDevice[]>
  deviceLookup: IdKeyedObjectShape<CustomerDevice>
  customMapLayers: ApiStatus<MapLayer[]>
  customMapLayerLookup: IdKeyedObjectShape<MapLayer>
}

type LoadAgencyAction = {
  type: 'LoadAgency'
  payload: LicensedAgencyViewModel
  timestamp: number | null
}

type LoadAgencyUsersAction = {
  type: 'LoadAgencyUsers'
  payload: UserViewModel[]
  timestamp: number | null
}

type LoadLicensedUsersAction = {
  type: 'LoadLicensedUsers'
  payload: CustomerUserViewModel[]
  timestamp: number | null
}

type LoadAdminUsersAction = {
  type: 'LoadAdminUsers'
  payload: CustomerAdminViewModel[]
  timestamp: number | null
}

type LoadDevicesAction = {
  type: 'LoadDevices'
  payload: CustomerDeviceViewModel[]
  timestamp: number | null
}

type ClearAction = {
  type: 'Clear'
}

type DeleteLicensedUserAction = {
  type: 'DeleteLicensedUser'
  payload: string
}

type AddLicensedUserAction = {
  type: 'AddLicensedUser'
  payload: CustomerUserViewModel
}

type EditLicensedUserAction = {
  type: 'EditLicensedUser'
  payload: CustomerUserViewModel
}

type DeleteAdminUserAction = {
  type: 'DeleteAdminUser'
  payload: string
}

type AddAdminUserAction = {
  type: 'AddAdminUser'
  payload: CustomerAdminViewModel
}

type EditAdminUserAction = {
  type: 'EditAdminUser'
  payload: CustomerAdminViewModel
}

type DeleteDeviceAction = {
  type: 'DeleteDevice'
  payload: string
}

type AddDeviceAction = {
  type: 'AddDevice'
  payload: CustomerDeviceViewModel
}

type EditDeviceAction = {
  type: 'EditDevice'
  payload: CustomerDeviceViewModel
}

type LoadCustomMapLayersAction = {
  type: 'LoadCustomMapLayers'
  payload: MapLayer[]
  timestamp: number | null
}

type DeleteCustomMapLayerAction = {
  type: 'DeleteCustomMapLayer'
  payload: string
}

type AddCustomMapLayerAction = {
  type: 'AddCustomMapLayer'
  payload: AddMapLayerResult
}

type EditCustomMapLayerAction = {
  type: 'EditCustomMapLayer'
  payload: EditMapLayerResult
}

export type AgencyAction =
  | LoadAgencyAction
  | LoadAgencyUsersAction
  | LoadAdminUsersAction
  | AddAdminUserAction
  | EditAdminUserAction
  | DeleteAdminUserAction
  | LoadLicensedUsersAction
  | AddLicensedUserAction
  | EditLicensedUserAction
  | DeleteLicensedUserAction
  | LoadDevicesAction
  | AddDeviceAction
  | EditDeviceAction
  | DeleteDeviceAction
  | ClearAction
  | LoadCustomMapLayersAction
  | AddCustomMapLayerAction
  | EditCustomMapLayerAction
  | DeleteCustomMapLayerAction

export interface CurrentAgencyContextState {
  state: CurrentAgencyState
  dispatch: (action: AgencyAction) => void
}

export const DEFAULT_AGENCY_STATUS: CurrentAgencyContextState = {
  state: {
    agency: {
      lastApiReceiveAt: null,
      data: null,
    },
    agencyUserLookup: {},
    agencyUsers: {
      lastApiReceiveAt: null,
      data: [],
    },
    licensedUserLookup: {},
    licensedUsers: {
      lastApiReceiveAt: null,
      data: [],
    },
    nonLicensedUsers: [],
    adminUserLookup: {},
    adminUsers: {
      lastApiReceiveAt: null,
      data: [],
    },
    nonAdminUsers: [],
    deviceLookup: {},
    devices: {
      lastApiReceiveAt: null,
      data: [],
    },
    customMapLayerLookup: {},
    customMapLayers: {
      lastApiReceiveAt: null,
      data: [],
    }
  } as CurrentAgencyState,
  dispatch: (action: AgencyAction) => {},
}

const reduceArrayToObject: <TType extends IdentifiableModel>(
  items: TType[],
) => IdKeyedObjectShape<TType> = (items) => {
  return items.reduce((accum, row) => ({ ...accum, [row.id]: row }), {})
}

const filterNonAdminUsers = (
  agencyUsers: User[] | null,
  admins: CustomerAdmin[],
) => {
  return (agencyUsers ?? []).filter(
    (x) => !admins.find((v) => v.userId === x.id),
  )
}

const filterNonLicensedUsers = (
  agencyUsers: User[] | null,
  licensed: CustomerUser[],
) => {
  return (agencyUsers ?? []).filter(
    (x) => !licensed.find((v) => v.userId === x.id),
  )
}

const reducer = (state: CurrentAgencyState, action: AgencyAction) => {
  switch (action.type) {
    case 'Clear': {
      return DEFAULT_AGENCY_STATUS.state
    }
    case 'LoadAgency': {
      return {
        ...state,
        agency: {
          data: action.payload,
          timestamp: action.timestamp,
        },
      }
    }
    case 'LoadAgencyUsers': {
      if (!state.agency.data) return state

      const users = action.payload.map((user) => asIdentifiable(user, user.id))
      return {
        ...state,
        agencyUserLookup: reduceArrayToObject(users),
        agencyUsers: {
          data: users,
          lastApiReceiveAt: action.timestamp,
        },
      }
    }
    case 'LoadAdminUsers': {
      if (!state.agency.data) return state

      const admins = action.payload.map((user) =>
        asIdentifiable(user, user.customerUserId),
      )
      const nonAdmins = filterNonAdminUsers(state.agencyUsers.data, admins)

      return {
        ...state,
        adminUserLookup: reduceArrayToObject(admins),
        adminUsers: {
          data: admins,
          lastApiReceiveAt: action.timestamp,
        },
        nonAdminUsers: nonAdmins,
      }
    }
    case 'LoadLicensedUsers': {
      if (!state.agency.data) return state

      const licensed = action.payload.map((user) =>
        asIdentifiable(user, user.id as string),
      )
      const nonLicensed = filterNonLicensedUsers(
        state.agencyUsers.data,
        licensed,
      )

      return {
        ...state,
        licensedUserLookup: reduceArrayToObject(licensed),
        licensedUsers: {
          data: licensed,
          lastApiReceiveAt: action.timestamp,
        },
        nonLicensedUsers: nonLicensed,
      }
    }
    case 'LoadDevices': {
      if (!state.agency.data) return state

      var devices = action.payload.map((device) =>
        asIdentifiable(device, device.customerDeviceId),
      )
      return {
        ...state,
        deviceLookup: reduceArrayToObject(devices),
        devices: {
          data: devices,
          lastApiReceiveAt: action.timestamp,
        },
      }
    }
    case 'AddLicensedUser': {
      if (!state.agency.data) return state

      const licensedUserLookup = {
        ...state.licensedUserLookup,
        [action.payload.id as string]: asIdentifiable(
          action.payload,
          action.payload.id as string,
        ),
      }

      const licensedUsers = idObjectToArray(licensedUserLookup)
      const nonLicensedUsers = filterNonLicensedUsers(
        state.agencyUsers.data,
        licensedUsers,
      )

      const newState = {
        ...state,
        licensedUserLookup: licensedUserLookup,
        licensedUsers: {
          ...state.licensedUsers,
          data: licensedUsers,
        },
        nonLicensedUsers: nonLicensedUsers,
      }

      updateAdminUsersPhones(newState, action.payload)
      return newState
    }
    case 'EditLicensedUser': {
      if (!state.agency.data) return state

      const licensedUserLookup = {
        ...state.licensedUserLookup,
        [action.payload.id as string]: asIdentifiable(
          action.payload,
          action.payload.id as string,
        ),
      }

      const newState: CurrentAgencyState = {
        ...state,
        licensedUserLookup: licensedUserLookup,
        licensedUsers: {
          ...state.licensedUsers,
          data: idObjectToArray(licensedUserLookup),
        },
      }

      updateAdminUsersPhones(newState, action.payload)
      return newState
    }
    case 'DeleteLicensedUser': {
      if (!state.agency.data) return state

      const licensedUsers = (state.licensedUsers.data ?? []).filter(
        (user) => user.id !== action.payload,
      )
      const nonLicensedUsers = filterNonLicensedUsers(
        state.agencyUsers.data,
        licensedUsers,
      )

      return {
        ...state,
        licensedUserLookup: reduceArrayToObject(licensedUsers),
        licensedUsers: {
          ...state.licensedUsers,
          data: licensedUsers,
        },
        nonLicensedUsers: nonLicensedUsers,
      }
    }
    case 'AddAdminUser': {
      if (!state.agency.data) return state

      const adminUserLookup = {
        ...state.adminUserLookup,
        [action.payload.customerUserId]: asIdentifiable(
          action.payload,
          action.payload.customerUserId,
        ),
      }

      const adminUsers = idObjectToArray(adminUserLookup)
      const nonAdminUsers = filterNonAdminUsers(
        state.agencyUsers.data,
        adminUsers,
      )

      const newState: CurrentAgencyState = {
        ...state,
        adminUserLookup: adminUserLookup,
        adminUsers: {
          ...state.adminUsers,
          data: adminUsers,
        },
        nonAdminUsers: nonAdminUsers,
      }

      updateNonAdminUsersPhones(newState, action.payload)
      return newState
    }
    case 'EditAdminUser': {
      if (!state.agency.data) return state

      const adminUserLookup = {
        ...state.adminUserLookup,
        [action.payload.customerUserId]: asIdentifiable(
          action.payload,
          action.payload.customerUserId,
        ),
      }

      const newState: CurrentAgencyState = {
        ...state,
        adminUserLookup: adminUserLookup,
        adminUsers: {
          ...state.adminUsers,
          data: idObjectToArray(adminUserLookup),
        },
      }

      updateNonAdminUsersPhones(newState, action.payload)
      return newState
    }
    case 'DeleteAdminUser': {
      if (!state.agency.data) return state

      const adminUsers = (state.adminUsers.data ?? []).filter(
        (x) => x.customerUserId != action.payload,
      )
      const nonAdminUsers = filterNonAdminUsers(
        state.agencyUsers.data,
        adminUsers,
      )

      return {
        ...state,
        adminUserLookup: reduceArrayToObject(adminUsers),
        adminUsers: {
          ...state.adminUsers,
          data: adminUsers,
        },
        nonAdminUsers: nonAdminUsers,
      }
    }
    case 'AddDevice': {
      if (!state.agency.data) return state

      const deviceLookup = {
        ...state.deviceLookup,
        [action.payload.customerDeviceId]: asIdentifiable(
          action.payload,
          action.payload.customerDeviceId,
        ),
      }

      return {
        ...state,
        deviceLookup: deviceLookup,
        devices: {
          ...state.devices,
          data: idObjectToArray(deviceLookup),
        },
      }
    }
    case 'EditDevice': {
      if (!state.agency.data) return state

      const deviceLookup = {
        ...state.deviceLookup,
        [action.payload.customerDeviceId]: asIdentifiable(
          action.payload,
          action.payload.customerDeviceId,
        ),
      }

      return {
        ...state,
        deviceLookup: deviceLookup,
        devices: {
          ...state.devices,
          data: idObjectToArray(deviceLookup),
        },
      }
    }
    case 'DeleteDevice': {
      if (!state.agency.data) return state

      const devices = (state.devices.data ?? []).filter(
        (x) => x.id != action.payload,
      )
      return {
        ...state,
        deviceLookup: reduceArrayToObject(devices),
        devices: {
          ...state.devices,
          data: devices,
        },
      }
    }
    case 'LoadCustomMapLayers': {
      if (!state.agency.data) return state

      var customMapLayers = action.payload.map((customMapLayer) =>
        asIdentifiable(customMapLayer, customMapLayer.id),
      )
      return {
        ...state,
        customMapLayerLookup: reduceArrayToObject(customMapLayers),
        customMapLayers: {
          data: customMapLayers,
          lastApiReceiveAt: action.timestamp,
        },
      }
    }
    case 'AddCustomMapLayer': {
      if (!state.agency.data) return state

      const customMapLayerLookup = {
        ...state.customMapLayerLookup,
        [action.payload.mapLayer.id]: asIdentifiable(
            action.payload.mapLayer,
            action.payload.mapLayer.id,
        ),
      }

      return {
        ...state,
        customMapLayerLookup: customMapLayerLookup,
        customMapLayers: {
          ...state.customMapLayers,
          data: idObjectToArray(customMapLayerLookup),
        },
      }
    }
    case 'EditCustomMapLayer': {
      if (!state.agency.data) return state

      const customMapLayerLookup = {
        ...state.customMapLayerLookup,
        [action.payload.mapLayer.id]: asIdentifiable(
            action.payload.mapLayer,
            action.payload.mapLayer.id,
        ),
      }

      return {
        ...state,
        customMapLayerLookup: customMapLayerLookup,
        customMapLayers: {
          ...state.customMapLayers,
          data: idObjectToArray(customMapLayerLookup),
        },
      }
    }
    case 'DeleteCustomMapLayer': {
      if (!state.agency.data) return state

      const mapLayers = (state.customMapLayers.data ?? []).filter(
          (x) => x.id != action.payload,
      )
      return {
        ...state,
        customMapLayerLookup: reduceArrayToObject(mapLayers),
        customMapLayers: {
          ...state.customMapLayers,
          data: mapLayers,
        },
      }
    }
    default:
      return state
  }
}

const updateNonAdminUsersPhones = (
  state: CurrentAgencyState,
  user: CustomerAdminViewModel,
) => {
  if (state.licensedUsers.data) {
    const licensedUserToUpdate = state.licensedUsers.data.find(
      (l) => l.userId === user.userId && l.phone !== user.phone,
    )

    if (licensedUserToUpdate) {
      licensedUserToUpdate.phone = user.phone
    }
  }

  if (state.nonLicensedUsers) {
    const nonLicensedUserToUpdate = state.nonLicensedUsers.find(
      (n) => n.id === user.userId && n.phone !== user.phone,
    )

    if (nonLicensedUserToUpdate) {
      nonLicensedUserToUpdate.phone = user.phone
    }
  }
}

const updateAdminUsersPhones = (
  state: CurrentAgencyState,
  user: CustomerUserViewModel,
) => {
  if (state.adminUsers.data) {
    const adminUserToUpdate = state.adminUsers.data.find(
      (a) => a.userId === user.userId && a.phone !== user.phone,
    )

    if (adminUserToUpdate) {
      adminUserToUpdate.phone = user.phone
    }
  }

  if (state.nonAdminUsers) {
    var nonAdminUserToUpdate = state.nonAdminUsers.find(
      (n) => n.id === user.userId && n.phone !== user.phone,
    )

    if (nonAdminUserToUpdate) {
      nonAdminUserToUpdate.phone = user.phone
    }
  }
}

export const getInitialAgency = () => DEFAULT_AGENCY_STATUS.state

export const CurrentAgencyContext = React.createContext<
  CurrentAgencyContextState
>(DEFAULT_AGENCY_STATUS)

type CurrentAgencyContextProviderProps = {
  children: any
}

export const CurrentAgencyContextProvider: React.FunctionComponent<CurrentAgencyContextProviderProps> = ({
  children,
}) => {
  const initialAssistedAgencyState = getInitialAgency()
  const [state, dispatch] = React.useReducer(
    reducer as React.Reducer<CurrentAgencyState, AgencyAction>,
    initialAssistedAgencyState,
  )
  const value = { state, dispatch }

  return (
    <CurrentAgencyContext.Provider value={value}>
      {children}
    </CurrentAgencyContext.Provider>
  )
}

export const CurrentAgencyContextConsumer = CurrentAgencyContext.Consumer
